/**
 * The board header.
 *
 * Contains the four things a user needs while looking at a board and nothing
 * else: what board this is, whether the connection is alive, who else is here,
 * and the two panels that are not the board itself.
 *
 * ## The board name is not an input
 *
 * Renaming a board is a deliberate act that happens in a menu, not by clicking on
 * the title. A title that turns into a text field when clicked is a well-liked
 * pattern and a poor one on a page where the title is also a drop target for
 * nothing in particular and where a misclick would put the board into an editing
 * state. The menu route is one extra click for a rare operation and removes an
 * accidental-edit failure mode entirely.
 *
 * ## The theme control is a three-way cycle
 *
 * Light, dark, system - cycling on click, with the current value in the label. A
 * two-state toggle cannot express "follow the system", which is the default and
 * the setting most people should be on. See `state/ThemeProvider.tsx`.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { Role } from '@slate/shared';
import { can } from '@slate/shared';
import { useBoard } from '../state/BoardProvider.js';
import { useTheme, type ThemePreference } from '../state/ThemeProvider.js';
import { ConnectionBadge } from './ConnectionBadge.js';
import { PresenceStack } from './PresenceStack.js';
import { Icon, Modal, ghostButtonClass, inputClass, dangerButtonClass } from './ui.js';

const THEME_ORDER: ThemePreference[] = ['system', 'light', 'dark'];
const THEME_LABEL: Record<ThemePreference, string> = {
  system: 'Theme: follow system',
  light: 'Theme: light',
  dark: 'Theme: dark',
};
const THEME_ICON = { system: 'monitor', light: 'sun', dark: 'moon' } as const;

export interface BoardHeaderProps {
  activityOpen: boolean;
  onToggleActivity(): void;
  /** The viewer's workspace role, used to hide controls the server would refuse.
   * The check here is the *same predicate* the server enforces - see `can()` in
   * `packages/shared/src/domain.ts` - so the UI cannot offer an action that will
   * 403. */
  role: Role;
  /** Called after a successful delete, so the shell can navigate away from a
   * board that no longer exists. */
  onBoardDeleted(): void;
}

export function BoardHeader({ activityOpen, onToggleActivity, role, onBoardDeleted }: BoardHeaderProps): ReactNode {
  const { state, actions } = useBoard();
  const { preference, setPreference } = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onPointerDown = (event: PointerEvent) => {
      if (menuRef.current !== null && !menuRef.current.contains(event.target as Node)) setMenuOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  const board = state.board;
  const archived = board?.archivedAt !== null && board?.archivedAt !== undefined;

  function cycleTheme(): void {
    const index = THEME_ORDER.indexOf(preference);
    setPreference(THEME_ORDER[(index + 1) % THEME_ORDER.length] ?? 'system');
  }

  return (
    <header className="flex flex-wrap items-center gap-3 border-b border-border bg-surface px-4 py-2.5">
      <div className="min-w-0">
        <h1 className="truncate text-sm font-semibold text-ink">{board?.name ?? 'Board'}</h1>
        {board?.description !== null && board?.description !== undefined && board.description !== '' && (
          <p className="truncate text-xs text-ink-muted">{board.description}</p>
        )}
      </div>

      {archived && (
        <span className="rounded-full bg-warning/15 px-2 py-0.5 text-[11px] font-medium text-warning">
          Archived
        </span>
      )}

      <div className="ml-auto flex items-center gap-3">
        <PresenceStack />
        <ConnectionBadge />

        <button
          type="button"
          onClick={cycleTheme}
          aria-label={THEME_LABEL[preference]}
          title={THEME_LABEL[preference]}
          className="rounded-md p-1.5 text-ink-muted hover:bg-sunken hover:text-ink"
        >
          <Icon name={THEME_ICON[preference]} size={16} />
        </button>

        <button
          type="button"
          onClick={onToggleActivity}
          aria-pressed={activityOpen}
          className={`${ghostButtonClass} ${activityOpen ? 'bg-sunken text-ink' : ''}`}
        >
          <Icon name="history" size={15} />
          Activity
        </button>

        {can(role, 'board:update') && (
          <div ref={menuRef} className="relative">
            <button
              type="button"
              onClick={() => setMenuOpen((open) => !open)}
              aria-expanded={menuOpen}
              aria-haspopup="menu"
              aria-label="Board options"
              className="rounded-md p-1.5 text-ink-muted hover:bg-sunken hover:text-ink"
            >
              <Icon name="chevron-down" size={16} />
            </button>
            {menuOpen && (
              <div role="menu" className="absolute right-0 z-30 mt-1 w-52 rounded-lg border border-border bg-raised py-1 shadow-lift">
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    void actions.archiveBoard(!archived);
                    setMenuOpen(false);
                  }}
                  className="block w-full px-3 py-1.5 text-left text-sm text-ink hover:bg-sunken"
                >
                  {archived ? 'Restore board' : 'Archive board'}
                </button>
                {can(role, 'board:delete') && (
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setConfirmDelete(true);
                      setMenuOpen(false);
                    }}
                    className="block w-full px-3 py-1.5 text-left text-sm text-danger hover:bg-danger-soft"
                  >
                    Delete board
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {confirmDelete && board !== null && (
        <Modal title={`Delete ${board.name}`} onClose={() => setConfirmDelete(false)}>
          <p className="text-sm text-ink-muted">
            This deletes the board, every column and card on it, and every attachment. It cannot be undone.
          </p>
          {/* A typed confirmation rather than a second "are you sure" click. The
              operation is irreversible and destroys other people's work, so the
              friction is the point. */}
          <form
            className="mt-4"
            onSubmit={(event) => {
              event.preventDefault();
              const data = new FormData(event.currentTarget);
              if (String(data.get('confirm') ?? '') !== board.name) return;
              void actions.deleteBoard().then((deleted) => {
                if (!deleted) return;
                setConfirmDelete(false);
                onBoardDeleted();
              });
            }}
          >
            <label htmlFor="confirm-board-name" className="block text-xs text-ink-muted">
              Type <span className="font-mono text-ink">{board.name}</span> to confirm
            </label>
            <input id="confirm-board-name" name="confirm" autoComplete="off" className={`${inputClass} mt-1`} />
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" className={ghostButtonClass} onClick={() => setConfirmDelete(false)}>
                Cancel
              </button>
              <button type="submit" className={dangerButtonClass}>
                Delete board
              </button>
            </div>
          </form>
        </Modal>
      )}
    </header>
  );
}
